import {Tooltip, TooltipContext, useTooltip} from "@/components/toolTip/Tooltip.tsx";
import {timezoneAbbr} from "@/lib/format.tsx";
import {StatusPageTimeFormat} from "@/lib/types/statuspages.ts";
import type {VirtualElement} from "@floating-ui/dom";
import type {ApexOptions} from "apexcharts";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Chart from "react-apexcharts";
import styles from "./ResponseTimeSparkline.module.scss";

// Chart dimensions match the Figma spec: 124px × 42px
const CHART_WIDTH = 124;
const CHART_HEIGHT = 42;

// Always green per Figma design
const CHART_LINE_COLOR = "#10b981";
const CHART_FILL_COLOR = "rgba(16, 185, 129, 0.25)";

export interface ResponseTimeSparklineProps {
  data: [string | number, number][];
  timezone?: string;
  timeFormat?: StatusPageTimeFormat;
}

function formatTooltipDate(timestampMs: number, tz?: string, timeFormat?: StatusPageTimeFormat): string {
  const resolvedTz = tz ?? "UTC";
  try {
    const formatted = new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      timeZone: resolvedTz,
      hourCycle: timeFormat === StatusPageTimeFormat.TWENTY_FOUR_HOUR ? "h23" : "h12",
    }).format(new Date(timestampMs));
    // timezoneAbbr (moment) supplies the abbreviation; Intl's own timeZoneName is omitted so it isn't doubled.
    return `${formatted} ${timezoneAbbr(resolvedTz, timestampMs)}`;
  } catch {
    return new Date(timestampMs).toLocaleString();
  }
}

export const ResponseTimeSparkline = ({data, timezone, timeFormat}: ResponseTimeSparklineProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{ts: number; valMs: number} | null>(null);
  const [theme, setTheme] = useState<string | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const themeRoot = el.closest("[data-theme]");
    if (!themeRoot) return;

    setTheme(themeRoot.getAttribute("data-theme"));
    const observer = new MutationObserver(() => {
      setTheme(themeRoot.getAttribute("data-theme"));
    });
    observer.observe(themeRoot, {attributes: true, attributeFilter: ["data-theme"]});
    return () => observer.disconnect();
  }, []);

  const tooltipContext = useTooltip({
    open: tooltip !== null,
    onOpenChange: (open) => {
      if (!open) setTooltip(null);
    },
    placement: "top",
    showArrow: false,
  });

  // Normalise timestamps to ms and values to ms response time
  const normalizedData = useMemo(
    () =>
      data.map(([ts, val]) => ({
        x: typeof ts === "string" ? new Date(ts).getTime() : (ts as number),
        y: parseFloat((val * 1000).toFixed(2)),
      })),
    [data]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!containerRef.current || normalizedData.length === 0) return;
      const rect = containerRef.current.getBoundingClientRect();
      const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const idx = Math.round(fraction * (normalizedData.length - 1));
      const point = normalizedData[idx];
      const virtualEl: VirtualElement = {
        getBoundingClientRect() {
          return {
            x: e.clientX,
            y: e.clientY,
            width: 0,
            height: 0,
            top: e.clientY,
            left: e.clientX,
            right: e.clientX,
            bottom: e.clientY,
          };
        },
      };
      tooltipContext.refs.setReference(virtualEl);
      setTooltip({ts: point.x, valMs: point.y});
    },
    [normalizedData, tooltipContext.refs]
  );

  const handlePointerLeave = useCallback(() => setTooltip(null), []);

  if (!data || data.length === 0) {
    return null;
  }

  const series = [{name: "Response Time", data: normalizedData}];

  const options: ApexOptions = {
    chart: {
      type: "area",
      toolbar: {show: false},
      zoom: {enabled: false},
      sparkline: {enabled: true},
      animations: {enabled: false},
      background: "transparent",
    },
    stroke: {
      curve: "smooth",
      width: 1.5,
      colors: [CHART_LINE_COLOR],
    },
    fill: {
      type: "solid",
      colors: [CHART_FILL_COLOR],
      opacity: 1,
    },
    colors: [CHART_LINE_COLOR],
    dataLabels: {enabled: false},
    tooltip: {enabled: false},
    xaxis: {
      type: "datetime",
      axisBorder: {show: false},
      axisTicks: {show: false},
      labels: {show: false},
    },
    yaxis: {
      labels: {show: false},
      axisBorder: {show: false},
    },
    grid: {show: false, padding: {left: 0, right: 0, top: 0, bottom: 0}},
    markers: {size: 0},
  };

  // The tooltip is rendered via FloatingPortal (inside TooltipContent) so it escapes
  // any overflow:hidden ancestor (e.g. the .componentItem card). Position is driven by
  // a virtual floating-ui reference element updated on each pointer move.
  return (
    <TooltipContext.Provider value={tooltipContext}>
      <div
        className="response-time-sparkline"
        ref={containerRef}
        aria-label="Response time chart"
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
      >
        <Chart type="area" width={CHART_WIDTH} height={CHART_HEIGHT} series={series} options={options} />
      </div>
      <Tooltip.Content variant="chart">
        {tooltip !== null && (
          <div {...(theme ? {"data-theme": theme} : {})}>
            <div className={styles.tooltipDate}>{formatTooltipDate(tooltip.ts, timezone, timeFormat)}</div>
            <div className={styles.tooltipLabel}>Response Time</div>
            <div className={styles.tooltipValue}>{Math.round(tooltip.valMs)}ms</div>
          </div>
        )}
      </Tooltip.Content>
    </TooltipContext.Provider>
  );
};
